import type { BlockVolume } from '@xver/apis/v1/block-storage/model';
import { ConfirmActionModal } from '@xver/components/Atoms';
import { guestUsageSnippets } from '@xver/utils/blockStorage';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useBlockStorageErrorText,
    useBlockVolumeMutations,
} from '../hooks/useBlockVolumeMutations';

interface DetachVolumeModalProps {
    /** null = closed. */
    volume: BlockVolume | null;
    onClose: () => void;
    /** Runs after a successful detach, once the dialog has closed; move focus off the row here. */
    onDetached?: () => void;
}

export default function DetachVolumeModal({ volume, onClose, onDetached }: DetachVolumeModalProps) {
    const { t } = useTranslation('blockStorage');
    const errorText = useBlockStorageErrorText();
    const { detachVolume } = useBlockVolumeMutations();
    const { reset, isPending, error } = detachVolume;

    useEffect(() => {
        if (volume) reset();
    }, [volume, reset]);

    const attachment = volume?.attachment;
    const name = volume?.name ?? '';

    return (
        <ConfirmActionModal
            opened={!!volume}
            onClose={() => {
                if (!isPending) onClose();
            }}
            title={t('detach.title', { name })}
            message={
                <div className="flex flex-col gap-2">
                    {attachment?.type === 'vm' ? (
                        <>
                            <p>
                                {t('detach.bodyVm', {
                                    target: attachment.hostname || t('detach.thisVm'),
                                })}
                            </p>
                            <code className="w-fit max-w-full px-2 py-1 rounded bg-fg/5 font-mono text-xs text-fg break-all">
                                {guestUsageSnippets(attachment.devicePath, name).unmount}
                            </code>
                        </>
                    ) : (
                        <p>
                            {t('detach.bodyContainer', {
                                target:
                                    (attachment?.type === 'container' &&
                                        attachment.containerName) ||
                                    t('detach.thisContainer'),
                            })}
                        </p>
                    )}
                    <p>{t('detach.dataKept')}</p>
                    {error && (
                        <p role="alert" className="text-fg">
                            {errorText(error)}
                        </p>
                    )}
                </div>
            }
            confirmLabel={t('detach.confirm')}
            confirmVariant="danger"
            isLoading={isPending}
            onConfirm={() => {
                if (volume)
                    detachVolume.mutate(volume, {
                        onSuccess: () => {
                            onClose();
                            onDetached?.();
                        },
                    });
            }}
        />
    );
}
